import { useCallback, useEffect, useState } from 'react';
import * as THREE from 'three';
import { MenuItem } from '../components/ContextMenu/ContextMenu';
import { ThreeRefs } from '../types';

interface ContextMenuState {
  isVisible: boolean;
  position: { top: number; left: number };
  menuItems: MenuItem[];
  title: string;
}

interface UseContextMenuProps {
  mountRef: React.RefObject<HTMLDivElement>;
  refs: ThreeRefs;
  getMenuItemsForMesh: (mesh: THREE.Mesh) => {
    items: MenuItem[];
    title: string;
  };
  getMenuItemsForMultipleMeshes?: (meshes: THREE.Mesh[]) => {
    items: MenuItem[];
    title: string;
  };
  isCameraControlsEnabled: boolean;
}

export const useContextMenu = ({
  mountRef,
  refs,
  getMenuItemsForMesh,
  getMenuItemsForMultipleMeshes,
  isCameraControlsEnabled,
}: UseContextMenuProps) => {
  const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({
    isVisible: false,
    position: { top: 0, left: 0 },
    menuItems: [],
    title: '',
  });

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuState((prev) => ({
      ...prev,
      isVisible: false,
    }));
  }, []);

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();

      // Don't show context menu if camera controls are enabled
      if (isCameraControlsEnabled) {
        return;
      }

      if (!mountRef.current || !refs.scene.current || !refs.camera.current) {
        return;
      }

      const rect = mountRef.current.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera({ x, y }, refs.camera.current);

      // Get all intersections and find the first interactive mesh
      const intersects = raycaster.intersectObjects(
        refs.scene.current.children,
        true
      );

      // Find the first mesh that is either interactive or has an interactive parent
      const interactiveMesh = intersects.reduce<THREE.Mesh | null>(
        (found, intersect) => {
          if (found) return found;

          let current = intersect.object;

          // Check if this mesh or any of its parents are interactive
          while (current && current.type !== 'Scene') {
            if (
              current instanceof THREE.Mesh &&
              current.userData?.isInteractive
            ) {
              return current;
            }
            current = current.parent!;
          }
          return null;
        },
        null
      );

      // Check if we have multiple selected meshes
      const selectedMeshes = refs.selectedMeshes.current;
      const hasMultipleSelections = selectedMeshes.length > 1;

      if (interactiveMesh) {
        let items: MenuItem[] = [];
        let title = 'Object';

        // If we have multiple selections and the clicked mesh is part of the selection,
        // show multi-selection menu
        if (hasMultipleSelections && selectedMeshes.includes(interactiveMesh)) {
          if (getMenuItemsForMultipleMeshes) {
            const multiMenu = getMenuItemsForMultipleMeshes(selectedMeshes);
            items = multiMenu.items;
            title = multiMenu.title;
          }
        } else {
          // Single mesh menu
          const singleMenu = getMenuItemsForMesh(interactiveMesh);
          items = singleMenu.items;
          title = singleMenu.title;
        }

        setContextMenuState({
          isVisible: true,
          position: {
            top: event.clientY,
            left: event.clientX,
          },
          menuItems: items,
          title,
        });
      }
    },
    [
      mountRef,
      refs.scene,
      refs.camera,
      refs.selectedMeshes,
      getMenuItemsForMesh,
      getMenuItemsForMultipleMeshes,
      isCameraControlsEnabled,
    ]
  );

  useEffect(() => {
    const canvasElement = mountRef.current;
    if (canvasElement) {
      canvasElement.addEventListener('contextmenu', handleContextMenu);
    }

    return () => {
      if (canvasElement) {
        canvasElement.removeEventListener('contextmenu', handleContextMenu);
      }
    };
  }, [mountRef, handleContextMenu]);

  return {
    contextMenuState,
    handleCloseContextMenu,
    handleContextMenu,
  };
};
